/* global NGXD */
// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Import child components elements.
import Items from './Items.jsx';
import PagingToolbar from './toolbars/PagingToolbar.jsx';
import SortingToolbar from './toolbars/SortingToolbar.jsx';
import FilteringToolbar from './toolbars/FilteringToolbar.jsx';

// Actions.
import Actions from '../actions';

// Selectors.
import Selectors from '../selectors';

// Other component dependencies.
import TabbedBrowsing from '../../../common/components/TabbedBrowsing';
import OverlayInterface from '../../common/Overlay/';
import NavigationInterface from '../../common/Navigation';
import { Header } from '../../../common/components/displayItems/document.jsx';
import * as overlayDisplayItems from '../../../common/components/displayItems/overlays.jsx';

// Misc dependencies.
import { assetFromRef } from 'display/common/utils/asset';
import { capitalize, getLabel } from '../../../common/utils/string';
import moduleUtils from '../utils/module-utils';
import _ from 'lodash';

import Enums from '../config/enums';
import { AnalyticsEvents as PUB } from '../../../common/config/api.js';

// Model objects.
import DisplayItem from '../../../common/model/entity/DisplayItem/DisplayItem';
import PinnedDisplayItem from '../../../common/model/entity/PinnedDisplayItem';

const AUTO_FETCH_INTERVAL = 15000;

/**
 * <pre>
 * Ugc Wall React component.
 *
 * Summary of responsibilities:
 *
 *  - smart component (connected to Redux store).
 *  - composes child components as required to output a Ugc Wall.
 *  - handles events received from child components and calls mapped dispatch functions as required.
 *
 * Basic component composition:
 *
 *  - Header:         </common/components/displayItems/document:Header>   Displays heading/subheading.
 *  - Sorting toolbar:    <./elements/toolbars:SortingToolbar>        Displays sorting related controls.
 *  - Filtering toolbar:  <./elements/toolbars:FilteringToolbar>        Displays filtering related controls.
 *  - Item list:      <./elements/Items>                  Displays list of display items.
 *  - Paging toolbar:     <./elements/toolbars:PagingToolbar>         Displays paging related controls.
 * </pre>
 *
 * @docPreview {namedExport: "docPreviewExport"}
 *
 * @memberof module:UgcWall.Components
 * @namespace
 *
 * @return {module:UgcWall.Components.UgcWall}
 */
class UgcWall extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Bind methods.
    this.fetchItems = this.fetchItems.bind(this);
    this.fetchPreviousItems = this.fetchPreviousItems.bind(this);
    this.pageItems = this.pageItems.bind(this);
    this.filterItems = this.filterItems.bind(this);
    this.sortItems = this.sortItems.bind(this);

    this.resize = this.resize.bind(this);
    this.getItemOverlayClass = this.getItemOverlayClass.bind(this);
    this.getItemById = this.getItemById.bind(this);
    this.getPinnedContentById = this.getPinnedContentById.bind(this);

    this.handleSort = this.handleSort.bind(this);
    this.handleFilter = this.handleFilter.bind(this);
    this.handleShowNext = this.handleShowNext.bind(this);
    this.handleShowPrevious = this.handleShowPrevious.bind(this);
    this.handleViewItem = this.handleViewItem.bind(this);
    this.handleCloseItem = this.handleCloseItem.bind(this);
    this.handleEnter = this.handleEnter.bind(this);
    this.clearReturnFocusTo = this.clearReturnFocusTo.bind(this);

    this.hasHeader = this.hasHeader.bind(this);
    this.getItemRenderMode = this.getItemRenderMode.bind(this);

    this.autoFetchInterval = undefined;
  }

  getChildContext() {
    return { config: this.props.config };
  }

  //  Keeping this around in case we want to conditionally render the header
  hasHeader() {
    //  there will be no header if all values of content are empty
    return _.filter(_.get(this.props.moduleItem, 'content', {}), (v, k) => !_.isEmpty(v)).length;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */

  /**
   * Called when component mounts for the first time.
   * Initiates request to fetch items.
   * Dispatches resize action when appropriate.
   */
  componentDidMount() {
    // Dispatch initial fetchItems action.
    this.fetchItems();

    // Dispatch resize action.
    this.resize();

    const showMoreMode = this.props.moduleOpts.showMoreMode;
    const shouldAutoPoll = this.props.moduleOpts.shouldAutoPoll;
    if (
      moduleUtils.allowAutoPolling(
        this.props.sorting,
        this.props.contentServicesVersion,
        showMoreMode,
        shouldAutoPoll,
        this.props.experienceVersion
      )
    ) {
      this.autoFetchInterval = setInterval(this.fetchPreviousItems, AUTO_FETCH_INTERVAL);
    }
  }

  componentWillUnmount() {
    clearInterval(this.autoFetchInterval);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // if filtering or sorting has changed, check if we will allow autoPolling
    const didAllowAutoPolling = moduleUtils.allowAutoPolling(
      this.props.sorting,
      this.props.contentServicesVersion,
      this.props.moduleOpts.showMoreMode,
      this.props.moduleOpts.shouldAutoPoll,
      this.props.experienceVersion
    );
    const willAllowAutoPolling = moduleUtils.allowAutoPolling(
      nextProps.sorting,
      nextProps.contentServicesVersion,
      nextProps.moduleOpts.showMoreMode,
      nextProps.moduleOpts.shouldAutoPoll,
      this.props.experienceVersion
    );

    if (didAllowAutoPolling && !willAllowAutoPolling) {
      clearInterval(this.autoFetchInterval);
    }
    if (!didAllowAutoPolling && willAllowAutoPolling) {
      this.autoFetchInterval = setInterval(this.fetchPreviousItems, AUTO_FETCH_INTERVAL);
    }
  }

  /**
   * Called whenever component is re-rendered.
   * Dispatches resize action when appropriate.
   */
  componentDidUpdate(prevProps) {
    // Dispatch resize action.
    this.resize();

    // Dispatch close action if an active item was opened externally.
    if (this.props.activeItem.id) {
      const renderMode = this.getItemRenderMode(this.props.activeItem.id, this.props.moduleOpts);
      if (renderMode === Enums.ITEM_SHOW_MODE_EXTERNAL) {
        this.handleCloseItem();
      }
    }

    // If the collection changed, re-fetch items.
    if (prevProps.collectionId !== this.props.collectionId) {
      this.fetchItems();
    }

    if (this.props.activeItem.returnFocusTo) {
      const itemEl = this.refs.componentInner.querySelector(`[data-ngx-id="${this.props.activeItem.returnFocusTo}"]`);
      itemEl && itemEl.focus();
    }
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.UgcWall}
   */
  render() {
    // Do we have any content items or any collage pins?
    let _hasItems = false;
    const isCollectionValid = this.props.collectionId !== 'INVALID_COLLECTION';
    if (this.props.moduleOpts.type === Enums.COLLAGE) {
      _hasItems =
        (this.props.hasFetched && (this.props.items.length > 0 || !_.isEmpty(this.props.moduleOpts.pinnedContent))) ||
        (!isCollectionValid && !_.isEmpty(this.props.moduleOpts.pinnedContent));
    } else {
      _hasItems = this.props.hasFetched && this.props.items.length > 0;
    }

    // Filtering toolbar (if enabled).
    let _filteringToolbar = '';
    if (this.props.moduleOpts.filter) {
      // Filtering related click handlers.
      const filteringClickHandlers = {
        filter: this.handleFilter,
      };

      _filteringToolbar = (
        <FilteringToolbar
          key="ft"
          filterConfig={this.props.moduleOpts.filterConfig}
          activeFilters={this.props.filtering}
          clickHandlers={filteringClickHandlers}
          contentServicesVersion={this.props.contentServicesVersion}
        />
      );
    }

    // Sorting toolbar (if enabled).
    let _sortingToolbar = '';
    if (this.props.moduleOpts.sort) {
      // Sorting related click handlers.
      const sortingClickHandlers = {
        sort: this.handleSort,
      };

      // If filtering is enabled, change the display mode of the sorting component from buttons (default), to select/combo style.
      const _sortingDisplayMode = _filteringToolbar ? Enums.SORT_DISPLAY_MODE_SELECT : Enums.SORT_DISPLAY_MODE_BUTTON;

      _sortingToolbar = (
        <SortingToolbar
          key="st"
          sortBy={this.props.sorting.sortBy}
          sortDir={this.props.sorting.sortDir}
          votingEnabled={this.props.itemOpts.enableVoting}
          clickHandlers={sortingClickHandlers}
          displayMode={_sortingDisplayMode}
          contentServicesVersion={this.props.contentServicesVersion}
        />
      );
    }

    // Top toolbar.
    // Acts as a wrapper for the filtering and sorting toolbars. If > 1 filtering elements are enabled
    // then wrap both toolbars so that theme can apply different styling.
    let _topToolbar;
    if (_filteringToolbar) {
      // Define additional classes for container.
      let _classes = 'xActionContainerWrapper xHasFiltering';
      if (_sortingToolbar) {
        _classes += ' xHasSorting';
      }

      _topToolbar = (
        <div className={_classes}>
          {' '}
          {_filteringToolbar} {_sortingToolbar}
        </div>
      );

      // Otherwise, just output directly underneath each other without wrapping.
    } else {
      _topToolbar = [_filteringToolbar, _sortingToolbar];
    }

    // Paging toolbar (if enabled).
    let _pagingToolbar = '';
    if (_hasItems && this.props.moduleOpts.shouldShowMore) {
      // Paging related click handlers.
      const pagingClickHandlers = {
        showMore:     this.handleShowNext,
        showNext:     this.handleShowNext,
        showPrevious: this.handleShowPrevious,
      };

      const allowAutoPolling = moduleUtils.allowAutoPolling(
        this.props.sorting,
        this.props.contentServicesVersion,
        this.props.moduleOpts.showMoreMode,
        this.props.moduleOpts.shouldAutoPoll,
        this.props.experienceVersion
      );
      _pagingToolbar = (
        <PagingToolbar
          currentPage={this.props.paging.currentPage}
          totalPages={this.props.paging.totalPages}
          itemCount={this.props.items.length}
          showMoreMode={this.props.moduleOpts.showMoreMode}
          clickHandlers={pagingClickHandlers}
          labels={this.props.labels.paging}
          allowAutoPolling={allowAutoPolling}
        />
      );
    }

    // Item list (if any items).
    let _ugcWallItems = '';

    if (_hasItems) {
      // Item related click handlers.
      const itemClickHandlers = {
        viewItem:     this.handleViewItem,
        externalLink: null,
      };

      // Pass reference to externalLinkHandler function down to child components only if
      // we need to use one.
      if (this.props.useExternalLinkHandler) {
        itemClickHandlers.externalLink = this.props.externalLinkHandler;
      }
      _ugcWallItems = (
        <Items
          id={this.props.id}
          items={this.props.items}
          customItems={this.props.customItems}
          moduleOpts={this.props.moduleOpts}
          templateOpts={this.props.templateOpts}
          itemOpts={this.props.itemOpts}
          clickHandlers={itemClickHandlers}
          pagingHandlers={{ showMore: this.handleShowNext }}
          currentPage={this.props.paging.currentPage}
          totalPages={this.props.paging.totalPages}
          showMoreMode={this.props.moduleOpts.showMoreMode}
          isVisible={this.props.isVisible}
          useExternalLinkHandler={this.props.useExternalLinkHandler}
          newItems={this.props.polling.newItems}
        />
      );

      // If we have already tried to fetch items, but returned no items, just output a message for now.
    } else if (!_hasItems && !this.props.isFetching) {
      // Default label.
      let label = getLabel('ugc.noitems');

      // If Content Services are enabled, and >= 2 (ie not legacy).
      if (this.props.allowContentServices && this.props.contentServicesVersion !== 'v1') {
        // If not entries module (where collectionId only exists once a published entry exists).
        if (!(this.props.moduleOpts.type === 'entries')) {
          // But no collection id is set...
          if (!this.props.collectionId) {
            label = getLabel('ugc.nocollectionset');
          }
        }
      }

      _ugcWallItems = (
        <div className="xNoUgcItems">
          <p> {label}</p>
        </div>
      );
    }

    // Activity icon whilst fetching data.
    let _fetchingActivity;
    if (this.props.isFetching) {
      _fetchingActivity = <div className="activityContainer isFetching" />;
    }

    // Show active/clicked item if required.
    let _overlay;
    if (this.props.activeItem.id) {
      // Get the item we want to show.
      const _item = this.getItemById(this.props.activeItem.id) || this.getPinnedContentById(this.props.activeItem.id);

      // Pass reference to externalLinkHandler function down to child components only if
      // we need to use one.
      const onExternalLinkClick = this.props.useExternalLinkHandler ? this.props.externalLinkHandler : null;

      // How are we going to show this item?
      const renderMode = this.getItemRenderMode(this.props.activeItem.id, this.props.moduleOpts);

      // Switch based on render mode.
      switch (renderMode) {
        // Show as overlay.
        case Enums.ITEM_SHOW_MODE_OVERLAY:
        default:
          // Define the Item component class to render.
          const _itemOverlayClass = this.getItemOverlayClass(this.props.templateOpts);
          // Generate overlay. Dispatch close item action as overlay close handler.
          _overlay = (
            <OverlayInterface.Components.Overlay closeHandler={this.handleCloseItem}>
              <_itemOverlayClass
                id={this.props.activeItem.id}
                item={_item}
                moduleOpts={this.props.moduleOpts}
                templateOpts={this.props.templateOpts}
                itemOpts={this.props.itemOpts}
                assetUrl={this.props.config.assetUrl}
                assetUgcUrl={this.props.config.assetUgcUrl}
                onExternalLinkClick={onExternalLinkClick}
              />
            </OverlayInterface.Components.Overlay>
          );

          break;

        // Link directly to external url.
        case Enums.ITEM_SHOW_MODE_EXTERNAL:
          const _url = _item.content.link;

          // If there is a valid registered external link handler.
          if (onExternalLinkClick) {
            // Call it, passing the url.
            this.props.externalLinkHandler(() => window.open(_url));
          } else {
            // Open in new window/tab.
            window.open(_url);
          }

          break;
      }
    }
    return (
      <Choose>
        <When condition={this.props.isVisible}>
          <TabbedBrowsing
            containerSelector={this.props.overlayCount > 0 ? '.xOverlay' : '.xPage'}
            onEnter={this.handleEnter}
            onTab={this.clearReturnFocusTo}
            onClick={this.clearReturnFocusTo}
          >
            <div ref="componentInner" className="xComponentInner">
              <Header item={this.props.moduleItem} />
              {_topToolbar}
              {_ugcWallItems}
              {_fetchingActivity}
              {_pagingToolbar}
              {_overlay}
            </div>
          </TabbedBrowsing>
        </When>
        <Otherwise>
          <div ref="componentInner" className="xComponentInner">
            <Header item={this.props.moduleItem} />
            {_topToolbar}
            {_ugcWallItems}
            {_fetchingActivity}
            {_pagingToolbar}
            {_overlay}
          </div>
        </Otherwise>
      </Choose>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
   * Handler called when sort button is clicked.
   *
   * @param {String} sortBy The sort column.
   * @param {String} sortDir The sort direction.
   */
  handleSort(sortBy, sortDir) {
    this.sortItems(sortBy, sortDir);
  }

  /**
   * Handler called when a user attempts to filter the content by some criteria (eg. username, content type, social networketc.).
   *
   * @param {Object} filters Object containing a list of filters.
   */
  handleFilter(filters) {
    this.filterItems(filters);
  }

  /**
   * Handler called when next button is clicked.
   *
   */
  handleShowNext() {
    // Notify API that the user wants to show more content
    if (this.props.triggerEvent) {
      this.props.triggerEvent(PUB.ACTION_SHOWMORE);
    }
    this.pageItems('next');
  }

  /**
   * Handler called when previous button is clicked.
   *
   */
  handleShowPrevious() {
    this.pageItems('prev');
  }

  /**
   * Handler called when individual item is clicked and we need to show it.
   *
   * @param {Common.Model.Entity.DisplayItem} item The clicked item.
   */
  handleViewItem(item) {
    if (this.props.triggerEvent) {
      let url = item.content.link;
      const asset = assetFromRef(item.assets.main, this.props.config.assetUrl, this.props.config.assetUgcUrl);
      if (!url && asset) {
        url = asset.src;
      }
      // API
      this.props.triggerEvent(PUB.ACTION_CONTENT_CLICK, { contentId: item.id, url });
    }
    // Dispatch action.
    this.props.dispatch.viewItem(this.props.id, this.props.moduleOpts.type, item.id);
  }

  /**
   * Handler called when individual item needs to be closed.
   *
   */
  handleCloseItem() {
    // Dispatch action.
    this.props.dispatch.closeItem(this.props.id, this.props.moduleOpts.type);
  }

  handleEnter(focusedElement) {
    // check if focusedElement is within current UgcWall instance so only a single overlay is opened
    const isFocusWithin = this.refs.componentInner.contains(focusedElement);
    if (!!focusedElement.dataset.ngxId && isFocusWithin) {
      const item =
        this.getItemById(focusedElement.dataset.ngxId) || this.getPinnedContentById(focusedElement.dataset.ngxId);
      this.handleViewItem(item);
    }
  }

  clearReturnFocusTo() {
    if (!this.props.activeItem.id && this.props.activeItem.returnFocusTo) {
      this.props.dispatch.clearReturnFocusTo(this.props.id, this.props.moduleOpts.type);
    }
  }

  /* Action dispatcher proxy methods */

  /**
   * Dispatch action to fetch items.
   *
   */
  fetchItems() {
    // Dispatch action.
    this.props.dispatch.fetchItems(
      this.props.id,
      this.props.moduleOpts.type,
      this.props.phase,
      this.props.moduleOpts.showMoreMode,
      this.props.moduleOpts.scope,
      this.props.moduleOpts.displayStrategy,
      this.props.itemOpts.socialVoting,
      null,
      null,
      this.props.moduleOpts.shouldShowStickyContent,
      this.props.moduleOpts.shouldAutoPoll
    );
  }

  // auto fetch newer content
  fetchPreviousItems() {
    if (!!this.props.collectionId) {
      this.props.dispatch.fetchPreviousItems(this.props.id, this.props.moduleOpts);
    }
  }

  /**
   * Dispatch action to page forward/backward through items.
   *
   * @param {String} direction The paging direction.
   */
  pageItems(direction) {
    // Dispatch action.
    this.props.dispatch.pageItems(
      this.props.id,
      this.props.moduleOpts.type,
      this.props.phase,
      this.props.moduleOpts.showMoreMode,
      this.props.moduleOpts.scope,
      this.props.moduleOpts.displayStrategy,
      this.props.itemOpts.socialVoting,
      direction,
      this.props.moduleOpts.shouldShowStickyContent,
      this.props.moduleOpts.shouldAutoPoll
    );
  }

  /**
   * Dispatch action to sort items.
   *
   * @param {String} sortBy The sort column.
   * @param {String} sortDir The sort direction.
   */
  sortItems(sortBy, sortDir) {
    // Dispatch action.
    this.props.dispatch.sortItems(
      this.props.id,
      this.props.moduleOpts.type,
      this.props.phase,
      this.props.moduleOpts.showMoreMode,
      this.props.moduleOpts.scope,
      this.props.moduleOpts.displayStrategy,
      this.props.itemOpts.socialVoting,
      sortBy,
      sortDir,
      this.props.items.length,
      this.props.moduleOpts.shouldShowStickyContent,
      this.props.moduleOpts.shouldAutoPoll
    );
  }

  /**
   * Dispatch action to filter items.
   *
   * @param {Object} filters The paging direction.
   */
  filterItems(filters) {
    // Dispatch action.
    this.props.dispatch.filterItems(
      this.props.id,
      this.props.moduleOpts.type,
      this.props.phase,
      this.props.moduleOpts.showMoreMode,
      this.props.moduleOpts.scope,
      this.props.moduleOpts.displayStrategy,
      this.props.itemOpts.socialVoting,
      filters,
      this.props.moduleOpts.displayCategory,
      this.props.items.length,
      this.props.moduleOpts.shouldShowStickyContent,
      this.props.moduleOpts.shouldAutoPoll
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Utility methods */

  /**
   * Waits for content to fully load, then dispatches resize API call (so parent frame can react accordingly).
   *
   */
  resize() {
    // Wait for images to finish loading, then resize.
    if (this.refs.componentInner) {
      imagesLoaded(this.refs.componentInner, () => {
        // Get Api service.
        const apiService = NGXD.getServiceContainer().getService('api');

        // Call resize method.
        apiService.execute('resizeParent');
      });
    }
  }

  /**
   * Utility method to generate css classnames for injection into the Overlay component based on the module options.
   *
   * @param  {Object} templateOpts Template options for the module.
   * @return {String} The css classnames.
   */
  getItemOverlayClass(templateOpts) {
    const _itemOverlayClassName = 'Overlay' + capitalize(templateOpts.details) + 'card';
    const _itemOverlayClass = overlayDisplayItems[_itemOverlayClassName];

    return _itemOverlayClass;
  }

  /**
   * Utility method to return an item object given its id.
   *
   * @param  {Integer} itemId The items id.
   * @return {Common.Model.Entity.DisplayItem} The matched item.
   */
  getItemById(itemId) {
    let item = null;

    item = this.props.items.filter(item => {
      return item.id === itemId;
    });

    return item[0];
  }
  /**
   * Utility method to return a pinned item object given its id.
   *
   * @param  {Integer} itemId The items id.
   * @return {Common.Model.Entity.DisplayItem} The matched item.
   */
  getPinnedContentById(itemId) {
    const pinnedContent = this.props.moduleOpts.pinnedContent;
    const item = pinnedContent[itemId];

    return new PinnedDisplayItem(
      item.id,
      { contentType: item.type, network: 'pinned' },
      { main: item.assets.main, thumbnail: item.assets.thumbnail }
    );
  }

  /**
   * Utility method to return the calculated render mode of an item when viewed.
   *
   * @param  {Integer} itemId The item id.
   * @param  {Object} moduleOpts Module options object.
   * @return {String} The matched item.
   */
  getItemRenderMode(itemId, moduleOpts) {
    const item = this.getItemById(itemId) || this.getPinnedContentById(itemId);

    // How are we going to show this item?
    let renderMode = moduleOpts.render;

    // WYN-3993 If item is ugc, then force overlay render mode.
    if (item.meta.network === 'ugc') {
      renderMode = Enums.ITEM_SHOW_MODE_OVERLAY;
    }

    return renderMode;
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} id The module DOM id.
 * @property {Object} moduleOpts Object defining the various module options.
 * @property {Object} templateOpts Object defining the various module template options.
 * @property {Object} itemOpts Object defining the various item options.
 * @property {Object} labels Object defining a k/v set of localized labels
 * @property {Object} moduleItem Object containing modules item content (eg. heading, byline etc).
 * @property {Array<Common.Model.Entity.DisplayItem>} items Immutable array containing the DisplayItem objects.
 */
UgcWall.propTypes = {
  id:                     PropTypes.string,
  moduleOpts:             PropTypes.object,
  templateOpts:           PropTypes.object,
  itemOpts:               PropTypes.object,
  labels:                 PropTypes.object,
  moduleItem:             PropTypes.object,
  items:                  PropTypes.arrayOf(PropTypes.instanceOf(DisplayItem)),
  contentServicesVersion: PropTypes.string, // v1, v2
  externalLinkHandler:    PropTypes.func,
};

/**
 * React default props.
 *
 * @type {Object}
 */
UgcWall.defaultProps = {};

/**
 * Maps Redux state values to React component props. Called automatically whenever
 * the bound state changes.
 *
 * @private
 *
 * @param  {Object} state The Redux state.
 * @param  {Object} props The current React component props.
 * @return {Object} Updated React component props.
 */
const mapStateToProps = (state, props) => {
  return {
    items:                  Selectors.getItems(state, props.moduleOpts.type, props.id),
    customItems:            Selectors.getCustomItems(state, props.moduleOpts.type, props.id),
    activeItem:             Selectors.getActiveItem(state, props.moduleOpts.type, props.id),
    paging:                 Selectors.getPaging(state, props.moduleOpts.type, props.id),
    sorting:                Selectors.getSorting(state, props.moduleOpts.type, props.id),
    filtering:              Selectors.getFiltering(state, props.moduleOpts.type, props.id),
    polling:                Selectors.getPolling(state, props.moduleOpts.type, props.id),
    isVisible:              NavigationInterface.Selectors.isModuleVisible(state, props.id),
    collectionId:           Selectors.getCollectionId(state, props.moduleOpts.type, props.id),
    useExternalLinkHandler: Selectors.getUseExternalLinkHandler(state, props.moduleOpts.type, props.id),
    isFetching:             Selectors.getIsFetching(state, props.moduleOpts.type, props.id),
    hasFetched:             Selectors.getHasFetched(state, props.moduleOpts.type, props.id),
    overlayCount:           OverlayInterface.Selectors.getTotalInstances(state),
  };
};

/**
 * Binds Redux action dispatches to React component props.
 *
 * @private
 *
 * @param  {Function} dispatch Dispatch context.
 * @return {Object} The mapped functions.
 */
const mapDispatchToProps = (dispatch, props) => {
  return {
    dispatch: {
      fetchItems: function(
        id,
        componentType,
        phase,
        showMoreMode,
        scope,
        displayStrategy,
        socialVoting,
        params,
        overridePhase,
        shouldShowStickyContent,
        shouldAutoPoll
      ) {
        dispatch(
          Actions.fetchItems(
            id,
            componentType,
            phase,
            showMoreMode,
            scope,
            displayStrategy,
            socialVoting,
            params,
            overridePhase,
            shouldShowStickyContent,
            shouldAutoPoll
          )
        );
      },
      fetchPreviousItems: (id, moduleOpts) => {
        dispatch(Actions.fetchPreviousItems(id, moduleOpts));
      },
      pageItems: function(
        id,
        componentType,
        phase,
        showMoreMode,
        scope,
        displayStrategy,
        socialVoting,
        direction,
        shouldShowStickyContent,
        shouldAutoPoll
      ) {
        dispatch(
          Actions.pageItems(
            id,
            componentType,
            phase,
            showMoreMode,
            scope,
            displayStrategy,
            socialVoting,
            direction,
            shouldShowStickyContent,
            shouldAutoPoll
          )
        );
      },
      sortItems: function(
        id,
        componentType,
        phase,
        showMoreMode,
        scope,
        displayStrategy,
        socialVoting,
        sortBy,
        sortDir,
        totalItems,
        shouldShowStickyContent,
        shouldAutoPoll
      ) {
        dispatch(
          Actions.sortItems(
            id,
            componentType,
            phase,
            showMoreMode,
            scope,
            displayStrategy,
            socialVoting,
            sortBy,
            sortDir,
            totalItems,
            shouldShowStickyContent,
            shouldAutoPoll
          )
        );
      },

      filterItems: function(
        id,
        componentType,
        phase,
        showMoreMode,
        scope,
        displayStrategy,
        socialVoting,
        filters,
        displayCategory,
        totalItems,
        shouldShowStickyContent,
        shouldAutoPoll
      ) {
        dispatch(
          Actions.filterItems(
            id,
            componentType,
            phase,
            showMoreMode,
            scope,
            displayStrategy,
            socialVoting,
            filters,
            displayCategory,
            totalItems,
            shouldShowStickyContent,
            shouldAutoPoll
          )
        );
      },

      viewItem: function(id, componentType, itemId) {
        dispatch(Actions.viewItem(id, componentType, itemId));
      },

      closeItem: function(id, componentType) {
        dispatch(Actions.closeItem(id, componentType));
      },
      clearReturnFocusTo: function(id, componentType) {
        dispatch(Actions.clearReturnFocusTo(id, componentType));
      },
    },
  };
};

UgcWall.childContextTypes = {
  config: PropTypes.object,
};

// Generate connected/smart component.
const UgcWallConnected = connect(mapStateToProps, mapDispatchToProps)(UgcWall);

// Export the connected/smart component as default.
export default UgcWallConnected;

// Also export the dumb component (for unit testing, doc preview etc).
export { UgcWall as docPreviewExport };
